import React, { useEffect, useState } from 'react';
import {createPortal} from 'react-dom';
import Attribution, {isAttributionVisible} from './Attribution';
import { GoogleMap, Marker, Polyline, InfoWindow, useGoogleMap } from '@react-google-maps/api';
import { loadGoogle, fitMapBounds, circleSymbol } from './providers/google';
import { useGeoConfig } from './config';
// Fachada de renderizado. Solo este adaptador importa los componentes del SDK de Google.
export { fitMapBounds, circleSymbol };
const mapProviders = { google: { MapCanvas: GoogleMap, MapMarker: Marker, MapPolyline: Polyline, MapInfoWindow: InfoWindow } };
function component(name) {
  return function GeoComponent(props) {
    const {config} = useGeoConfig(); const Component = mapProviders[config.services.map]?.[name];
    return Component ? <Component {...props} {...(name === 'MapCanvas' ? {options: {mapId: config.google.map_id, ...props.options}} : {})} /> : null;
  };
}
export const MapCanvas = component('MapCanvas');
export const MapMarker = component('MapMarker');
export const MapPolyline = component('MapPolyline');
export const MapInfoWindow = component('MapInfoWindow');
export function useMapLoader({enabled = true} = {}) {
  const { config, ready, reloadRequired } = useGeoConfig();
  const [state, setState] = useState({ isLoaded:false, loadError:null });
  useEffect(() => {
    if (!ready || !enabled) return;
    let alive = true;
    loadGoogle().then(() => { if(alive) setState({isLoaded:true,loadError:null}); })
      .catch(error => { if(alive) setState({isLoaded:false,loadError:error}); });
    return () => { alive = false; };
  }, [ready, config.version, enabled]);
  return {...state, isLoaded: enabled && state.isLoaded, version: config.version, reloadRequired};
}
export function MapAttribution({attribution}) {
  const visible = isAttributionVisible(attribution);
  const map = useGoogleMap();
  const [element] = useState(()=>document.createElement('div'));
  useEffect(()=>{
    if(!map || !visible) return;
    element.style.cssText='background:white;color:#333;padding:3px 8px;margin:4px;max-width:260px;';
    const controls=map.controls[window.google.maps.ControlPosition.LEFT_BOTTOM];
    controls.push(element);
    return ()=>{const index=controls.getArray().indexOf(element);if(index>=0)controls.removeAt(index);};
  },[map,element,visible]);
  return visible ? createPortal(<Attribution attribution={attribution} />,element) : null;
}
function GoogleRouteLayer({ directions, options = {} }) {
  const map = useGoogleMap();
  const coordinates = directions?.geometry?.coordinates;
  const [activeEndpoint,setActiveEndpoint] = useState(null);
  useEffect(()=>{setActiveEndpoint(null);},[directions]);
  useEffect(() => {
    if (!options.preserveViewport && coordinates?.length) fitMapBounds(map,coordinates.map(([lng,lat])=>({lat,lng})));
  }, [map,coordinates,options.preserveViewport]);
  if (!coordinates?.length) return null;
  const path = coordinates.map(([lng,lat])=>({lat,lng}));
  return <><MapAttribution attribution={directions.attribution} /><Polyline path={path} options={options.polylineOptions} />
    {!options.suppressMarkers && <>
      <Marker position={path[0]} label="A" title={directions.start_address || 'Inicio'} onClick={()=>setActiveEndpoint('start')} />
      <Marker position={path[path.length-1]} label="B" title={directions.end_address || 'Fin'} onClick={()=>setActiveEndpoint('end')} />
      {activeEndpoint && <InfoWindow position={activeEndpoint==='start'?path[0]:path[path.length-1]} onCloseClick={()=>setActiveEndpoint(null)}>
        <div style={{maxWidth:280}}><strong>{activeEndpoint==='start'?'Inicio':'Fin'}</strong><div>{directions[`${activeEndpoint}_address`] || 'Dirección no disponible'}</div></div>
      </InfoWindow>}
    </>}
  </>;
}
export const RouteLayer = GoogleRouteLayer;
export function StreetView({ position }) {
  const [element,setElement] = useState(null);
  useEffect(() => {
    if(!element || !position) return;
    let panorama; let alive = true;
    loadGoogle().then(google => { if(alive) panorama = new google.maps.StreetViewPanorama(element, {position, pov:{heading:165,pitch:0}, zoom:1, visible:true}); }).catch(()=>{});
    return () => { alive = false; if(panorama) panorama.setVisible(false); };
  },[element,position]);
  return <div ref={setElement} style={{height:350}} aria-label="Vista de calle" />;
}
